import { useState, useEffect } from 'react';
import { Dialog, HelpTip, PANEL_HELP, api, useToast } from './_barrel.js';


        // ===== Sign-off Dialog =====
        // Presents the formal statement, lets the analyst edit it,
        // captures their role and confirms classification, then submits.
        function ReportSignoffDialog({ token, report, onClose, onSuccess }) {
            const DEFAULT_STATEMENT = "I, the named analyst below, having reviewed the data, analysis, and narrative produced for this report, attest that this assessment represents my professional judgement as of the date below, is based on publicly available information and the analytic methods described, and may be relied upon for protective intelligence purposes within the classification marking applied to this document.";
            const [statement, setStatement] = useState(DEFAULT_STATEMENT);
            const [role, setRole] = useState('Lead analyst');
            const [classification, setClassification] = useState('client_eyes_only');
            const [error, setError] = useState(null);
            const [submitting, setSubmitting] = useState(false);
            const toast = useToast();

            useEffect(() => {
                (async () => {
                    try {
                        const d = await api(`/api/v1/reports/${report.id}/signoff/default-statement`, {}, token);
                        if (d?.statement) setStatement(d.statement);
                    } catch (e) { /* keep the hardcoded default */ }
                })();
            }, [token, report.id]);

            const submit = async () => {
                if (statement.trim().length < 20) {
                    setError('Statement must be at least 20 characters.');
                    return;
                }
                setSubmitting(true);
                setError(null);
                try {
                    await api(`/api/v1/reports/${report.id}/signoff`,
                        { method: 'POST',
                          body: JSON.stringify({ statement, role, classification }) },
                        token);
                    toast('Report signed off');
                    onSuccess?.();
                } catch (e) { setError(e.message); }
                finally { setSubmitting(false); }
            };

            return (
                <div style={{position:'fixed', inset:0, background:'rgba(5,9,16,0.72)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
                    <div style={{background:'var(--bg)', border:'1px solid var(--gold)', maxWidth:'700px', width:'100%', maxHeight:'90vh', overflowY:'auto'}}>
                        <div className="panel-header">
                            <div>
                                <div className="font-display text-xl gold">Sign Off Report<HelpTip text={PANEL_HELP.sign_off} /></div>
                                <div className="text-xs text-muted font-mono mt-1">
                                    {report.report_number || report.id.slice(0,8)} · This action cannot be undone.
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-5 space-y-4">
                            {error && <div className="text-[var(--red)] text-sm">{error}</div>}

                            <div className="text-sm text-muted">
                                By signing off, you bind your name to the exact wording below. The classification marking you choose will appear on every page of the final report. The DRAFT watermark will be removed.
                            </div>

                            <div>
                                <label className="text-xs">Your role at sign-off</label>
                                <input type="text" value={role} onChange={(e) => setRole(e.target.value)}
                                    placeholder="e.g. Lead analyst, Senior consultant" />
                            </div>

                            <div>
                                <label className="text-xs">Classification</label>
                                <select value={classification} onChange={(e) => setClassification(e.target.value)}>
                                    <option value="client_eyes_only">CLIENT EYES ONLY</option>
                                    <option value="official_sensitive">OFFICIAL SENSITIVE</option>
                                    <option value="official">OFFICIAL</option>
                                </select>
                            </div>

                            <div>
                                <label className="text-xs">Statement you attest to</label>
                                <textarea rows="8" value={statement}
                                    onChange={(e) => setStatement(e.target.value)}
                                    placeholder="Edit the standard statement above, or replace it with bespoke wording."
                                    style={{width:'100%', resize:'vertical', background:'var(--zone-2)', color:'var(--text)', border:'1px solid var(--veil)', padding:'10px', fontFamily:'inherit', fontSize:'13px'}}
                                />
                                <div className="text-xs text-muted mt-1">
                                    {statement.length} characters · minimum 20
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-3 border-t border-veil">
                                <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                                <button type="button" className="btn-gold" onClick={submit} disabled={submitting}>
                                    {submitting ? 'Signing…' : 'Sign and Lock Report'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

export { ReportSignoffDialog };
